import { FC, SyntheticEvent } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { FormikProps } from 'formik';

import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers';

import {
  emptyLottery,
  LOTTERY_DESCRIPTION_MAX,
  LOTTERY_PROMO_MAX,
  LotteryFormSection,
  PARTNER_SPOTLIGHT_IMAGE_MAX_SIZE,
  PartnerSpotlightForm,
} from '../form';
import { graphemeLength } from '../utils';

import css from './PartnerSpotlighFormLotterySection.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Switch } from 'v2/components/switch/Switch';

interface PartnerSpotlighFormLotterySectionProps {
  formik: FormikProps<PartnerSpotlightForm>;
  disabled?: boolean;
}

export const PartnerSpotlighFormLotterySection: FC<PartnerSpotlighFormLotterySectionProps> = ({
  formik,
  disabled = false,
}) => {
  const lottery = formik.values.lottery;
  const lotteryErrors = (formik.errors.lottery ?? {}) as Partial<Record<keyof LotteryFormSection, string>>;

  const handleToggle = (_: SyntheticEvent, checked: boolean) => {
    formik.setFieldValue('lotteryEnabled', checked);
    if (!checked) {
      formik.setFieldValue('lottery', emptyLottery);
    }
  };

  const handleLotteryFieldChange = (field: keyof LotteryFormSection) => (value: string) => {
    formik.setFieldValue(`lottery.${field}`, value);
  };

  const handleLotteryDateChange = (value: Dayjs | null) => {
    formik.setFieldValue('lottery.endDate', value);
  };

  return (
    <div className={css.partnerSpotlighFormLotterySection}>
      <Switch
        name="lotteryEnabled"
        checked={formik.values.lotteryEnabled}
        onChange={handleToggle}
        label="Lottery"
        disabled={disabled}
      />

      {formik.values.lotteryEnabled && !disabled && (
        <div className={css.partnerSpotlighFormLotterySectionContent}>
          <FileSelector
            value={lottery.imageId}
            label="Picture"
            onSelect={handleLotteryFieldChange('imageId')}
            error={lotteryErrors.imageId}
            maxSize={PARTNER_SPOTLIGHT_IMAGE_MAX_SIZE}
            required
          />

          <TextField
            name="lottery.description"
            value={lottery.description}
            onChange={(e) => handleLotteryFieldChange('description')(e.target.value)}
            label="Description"
            error={Boolean(lotteryErrors.description)}
            helperText={
              lotteryErrors.description ?? `${graphemeLength(lottery.description)}/${LOTTERY_DESCRIPTION_MAX}`
            }
            required
          />

          <div className="form-group">
            <TextField
              name="lottery.promo1"
              value={lottery.promo1}
              onChange={(e) => handleLotteryFieldChange('promo1')(e.target.value)}
              label="Promo 1"
              error={Boolean(lotteryErrors.promo1)}
              helperText={lotteryErrors.promo1 ?? `${graphemeLength(lottery.promo1)}/${LOTTERY_PROMO_MAX}`}
              fullWidth
            />

            <TextField
              name="lottery.promo2"
              value={lottery.promo2}
              onChange={(e) => handleLotteryFieldChange('promo2')(e.target.value)}
              label="Promo 2"
              error={Boolean(lotteryErrors.promo2)}
              helperText={lotteryErrors.promo2 ?? `${graphemeLength(lottery.promo2)}/${LOTTERY_PROMO_MAX}`}
              fullWidth
            />
          </div>

          <DateTimePicker
            value={lottery.endDate}
            onChange={handleLotteryDateChange}
            label="End Date *"
            format="DD.MM.YYYY HH:mm"
            minDateTime={dayjs()}
            slotProps={{
              textField: {
                error: Boolean(lotteryErrors.endDate),
                helperText: lotteryErrors.endDate as string | undefined,
              },
            }}
          />

          {typeof formik.errors.lottery === 'string' && (
            <Typography color="error" variant="caption">
              {formik.errors.lottery}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
